import joi from 'joi';
import { Add, Delete } from '@mui/icons-material';
import { Box, Button, Grid, IconButton } from '@mui/material';
import { DragDropContext, Draggable, DropResult, Droppable } from 'react-beautiful-dnd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { QlfSpinner } from '@qualifio/ui-commons';
import { TabsComponentAttributes, TabsComponentI18nAttributes } from '@loyalty/editor-components';
import { faGripVertical } from '@fortawesome/pro-solid-svg-icons/faGripVertical';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { InputTextField } from '../../../../shared/forms/fields/InputTextField';
import { Trait } from '../utils/Trait';
import { TraitFormValue } from '../types';
import { useEditorStore } from '../../../store';

export const tabsWidgetTraitsSchema = joi.object<TraitFormValue>({
  attributes: joi.object<TabsComponentAttributes>({
    tabs: joi.array().items(
      joi.object({
        contentId: joi.string().allow(null),
      }),
    ),
  }),
  i18nAttributes: joi.object<TabsComponentI18nAttributes>({
    tabLabels: joi.object().pattern(joi.string(), joi.string()),
  }),
});

type FormValue = TraitFormValue<TabsComponentAttributes, TabsComponentI18nAttributes>;

export function TabsWidgetTraits() {
  const { t } = useTranslation();

  // Use Zustand store
  const selectedComponent = useEditorStore(state => state.selectedComponent);

  const { control, watch } = useFormContext<FormValue>();
  const { fields, append, remove, move } = useFieldArray({
    control,
    name: 'attributes.tabs',
  });

  const tabs = watch('attributes.tabs') || [];
  const tabLabels = watch('i18nAttributes.tabLabels') || {};

  const handleAddTab = async () => {
    // The addTabContent method will create a new tab-content component and return the id of the new component
    const newContentId = selectedComponent?.view.addTabContent();
    append({ contentId: newContentId });
  };

  const handleRemoveTab = (index: number) => {
    const tabToRemove = tabs[index];
    // The removeTabContent method will remove the tab-content component
    selectedComponent?.view.removeTabContent(tabToRemove.contentId);
    remove(index);
  };

  const handleDragEnd = ({ source, destination }: DropResult) => {
    if (destination && source.index !== destination.index) {
      move(source.index, destination.index);
    }
  };

  return (
    <Box>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="tabs-list">
          {({ placeholder, droppableProps, innerRef }) => (
            <Box {...droppableProps} ref={innerRef}>
              {fields.map((field, index) => {
                const tab = tabs[index];
                const contentId = tab?.contentId;
                const hasContentId = !!contentId;
                const tabLabel = hasContentId ? tabLabels[contentId] : undefined;

                return (
                  <Draggable key={field.id} draggableId={field.id} index={index}>
                    {({ innerRef, draggableProps, dragHandleProps }) => (
                      <Trait ref={innerRef} {...draggableProps}>
                        <Grid container spacing={1} alignItems="center">
                          <Grid item>
                            <Box
                              {...dragHandleProps}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                cursor: 'grab',
                                color: 'grey.500',
                                '&:hover': { color: 'grey.700' },
                              }}
                            >
                              <FontAwesomeIcon icon={faGripVertical} />
                            </Box>
                          </Grid>
                          <Grid item xs>
                            {hasContentId ? (
                              <InputTextField
                                name={`i18nAttributes.tabLabels.${contentId}`}
                                label={t('editor.component.tabs.label', {
                                  index: index + 1,
                                  defaultValue: `Tab ${index + 1}`,
                                })}
                                defaultValue={tabLabel || `Tab ${index + 1}`}
                                fullWidth
                                size="small"
                              />
                            ) : (
                              <QlfSpinner />
                            )}
                          </Grid>
                          <Grid item>
                            <IconButton onClick={() => handleRemoveTab(index)} size="small">
                              <Delete fontSize="small" />
                            </IconButton>
                          </Grid>
                        </Grid>
                      </Trait>
                    )}
                  </Draggable>
                );
              })}
              {placeholder}
            </Box>
          )}
        </Droppable>
      </DragDropContext>
      <Box sx={{ p: 1, mt: 1 }}>
        <Button startIcon={<Add />} onClick={handleAddTab} fullWidth>
          {t('editor.component.tabs.add', { defaultValue: 'Add Tab' })}
        </Button>
      </Box>
    </Box>
  );
}
